import React from 'react';
import { ColumnMeta, RowData } from '@tanstack/react-table';
import dayjs from 'dayjs';
import styled, { css, CSSObject } from 'styled-components';
import { prop } from 'styled-tools';
import { SmallTooltip } from '../Tooltip';
import { v4 as uuidv4 } from 'uuid';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    isCentered?: boolean;
    styles?: CSSObject;
  }
}

export const TableWrapper = styled.div``;

export const Table = styled.table`
  background-color: ${prop('theme.colors.bgDefaultSecondary')};
  border-radius: 10px;
  padding-bottom: 0;
  border-collapse: inherit;
  border-spacing: 0px;

  th {
    background-color: ${prop('theme.colors.bgDefaultSecondary')};
  }

  thead > tr > th:first-child {
    border-top-left-radius: 8px;
    border-left: 1px solid ${prop('theme.colors.bgActive')};
  }

  thead > tr > th:last-child {
    border-top-right-radius: 8px;
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  thead > tr:last-child > th {
    border-top: 1px solid ${prop('theme.colors.bgActive')};
    border-bottom: 1px solid ${prop('theme.colors.bgActive')};
  }

  tbody > tr:last-child > td:first-child {
    border-bottom-left-radius: 8px;
    overflow: hidden;
  }

  tbody > tr:last-child > td:last-child {
    border-bottom-right-radius: 8px;
  }

  tbody > tr > td:first-child {
    border-left: 1px solid ${prop('theme.colors.bgActive')};
  }

  tbody > tr > td:last-child {
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  tbody > tr:last-child > td {
    border-bottom: 1px solid ${prop('theme.colors.bgActive')};
  }
`;

export const TableHeader = styled.thead`
  border-bottom: 1px solid ${prop('theme.colors.bgActive')};
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr<{ isSelected?: boolean }>`
  transition: all 0.3s ease-in-out;
  background-color: ${({ isSelected }) =>
    isSelected ? prop('theme.colors.inputBackground') : 'transparent'};
`;

export const TableHead = styled.th<{
  meta?: ColumnMeta<unknown, unknown>;
}>`
  font-weight: bold;
  text-align: ${({ meta }) => (meta?.isCentered ? 'center' : 'left')};

  &:not(:last-child) {
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  ${({ meta }) => meta?.styles && css(meta.styles)}
`;

export const TableCell = styled.td<{ meta?: ColumnMeta<unknown, unknown> }>`
  color: ${prop('theme.colors.textActive')};
  ${({ meta }) => meta?.isCentered && `text-align: center;`}

  &:not(:last-child) {
    border-right: 1px solid ${prop('theme.colors.bgActive')};
  }

  ${({ meta }) => meta?.styles && css(meta.styles)}
`;

export const ClickableTableRow = styled(TableRow)`
  transition: all 0.3s ease-in-out;

  &:hover {
    background-color: ${prop('theme.colors.bgDefault')};
  }
  td {
    cursor: pointer;
  }
`;

export const DateTableCellContent = ({ date }: { date: Date | null }) => {
  const id = React.useMemo(() => uuidv4(), []);

  if (!date) {
    return <span className={id}>-</span>;
  }

  const fromNow = dayjs.utc(date).fromNow();
  const dateString = dayjs.utc(date).format('YYYY-MM-DD HH:mm');

  return (
    <>
      <div data-tooltip-id={id}>{dateString}</div>
      <SmallTooltip anchorSelect={`[data-tooltip-id="${id}"]`}>
        {fromNow}
      </SmallTooltip>
    </>
  );
};
